import type { ReactNode } from 'react'
import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react'

import { useRoles } from '@fourthwall/auth'
import { useSessionStorage } from '@fourthwall/components'

type AdminModeContextType = {
  isActive: boolean
  isInternalAdmin: boolean
  toggleActive: () => void
}

export const AdminModeContext = createContext<AdminModeContextType | undefined>(undefined)

type Props = {
  children: ReactNode
}

export const AdminModeProvider = ({ children }: Props) => {
  const { isLoadingRoles, hasRole } = useRoles()
  const isInternalAdmin = hasRole('ROLE_FOURTHWALL_INTERNAL_ADMIN')
  const [isActive, setIsActive] = useSessionStorage('isActive', isInternalAdmin)

  useEffect(() => {
    if (!isLoadingRoles) {
      setIsActive(isInternalAdmin)
    }
  }, [isLoadingRoles])

  const toggleActive = useCallback(() => {
    const newValue = !isActive
    setIsActive(newValue)
  }, [isActive, setIsActive])

  const value = useMemo(
    () => ({ isActive: isActive && isInternalAdmin, toggleActive, isInternalAdmin }),
    [hasRole, isActive, isInternalAdmin, toggleActive],
  )

  return <AdminModeContext.Provider value={value}>{children}</AdminModeContext.Provider>
}

export const useAdminModeContext = () => {
  return useContext(AdminModeContext)
}
